import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Header } from '@/components/layout/Header';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ToggleRow } from '@/components/settings/ToggleRow';
import { TagFilterChips } from '@/components/settings/TagFilterChips';
import { WeddingDetailsForm } from '@/components/settings/WeddingDetailsForm';
import { ShareSection } from '@/components/settings/ShareSection';
import { ExportSection } from '@/components/settings/ExportSection';
import { usePreferences } from '@/hooks/usePreferences';
import { useInitWedding, useWedding, useUpdateWedding } from '@/hooks/useWedding';
import { useToast } from '@/hooks/useToast';
import { api } from '@/lib/api';
import { clearWedding, clearEvents } from '@/lib/storage';
import type { EventCategory } from '@/lib/types';

export function SettingsPage() {
  const { prefs, updatePreference } = usePreferences();
  const { weddingId } = useInitWedding();
  const { data: wedding } = useWedding(weddingId);
  const updateWedding = useUpdateWedding(weddingId ?? '');
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const toggleLeadTime = (minutes: number, on: boolean) => {
    const current = prefs.notifyLeadTimes;
    const next = on ? [...current, minutes] : current.filter((t) => t !== minutes);
    updatePreference({ notifyLeadTimes: next });
  };

  const handleReset = async () => {
    if (!weddingId) return;
    setIsResetting(true);
    try {
      await api.delete(`/api/weddings/${weddingId}`);
      await clearWedding();
      await clearEvents();
      queryClient.clear();
      navigate('/schedule');
    } catch {
      toast('Failed to reset. Please try again.', 'error');
    } finally {
      setIsResetting(false);
      setShowResetDialog(false);
    }
  };

  const sectionHeader = (label: string) => (
    <p
      className="text-xs uppercase tracking-widest mt-6 mb-2"
      style={{ color: 'var(--color-text-muted)', letterSpacing: '1.5px' }}
    >
      {label}
    </p>
  );

  return (
    <div className="flex flex-col min-h-screen" style={{ background: 'var(--color-background)' }}>
      <Header title="Settings" />
      <main className="flex-1 px-4 pb-24">
        {sectionHeader('Wedding Details')}
        <WeddingDetailsForm
          wedding={wedding ?? null}
          onUpdate={(data) =>
            weddingId ? updateWedding.mutate(data) : undefined
          }
        />

        {sectionHeader('Notifications')}
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-surface)' }}>
          {[
            { minutes: 30, label: '30 min before' },
            { minutes: 10, label: '10 min before' },
            { minutes: 5, label: '5 min before' },
            { minutes: 0, label: 'Event start (NOW)' },
          ].map(({ minutes, label }, i) => (
            <div
              key={minutes}
              className={i > 0 ? 'border-t' : ''}
              style={{ borderColor: 'rgba(255,255,255,0.08)' }}
            >
              <ToggleRow
                label={label}
                checked={prefs.notifyLeadTimes.includes(minutes)}
                onChange={(on) => toggleLeadTime(minutes, on)}
              />
            </div>
          ))}
          <div className="border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
            <ToggleRow
              label="Quiet Mode"
              description={prefs.quietMode ? 'All notifications silenced' : undefined}
              checked={prefs.quietMode}
              onChange={(on) => updatePreference({ quietMode: on })}
              highlight={prefs.quietMode ? 'yellow' : undefined}
            />
          </div>
        </div>

        {sectionHeader('Tag Filter')}
        <TagFilterChips
          selected={prefs.categoryFilter}
          onChange={(cats) => updatePreference({ categoryFilter: cats as EventCategory[] })}
        />
        <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
          Only notify for selected categories
        </p>

        {sectionHeader('Share Timeline')}
        {weddingId && <ShareSection weddingId={weddingId} wedding={wedding ?? null} />}

        {sectionHeader('Export')}
        {weddingId && <ExportSection weddingId={weddingId} tags={prefs.categoryFilter} />}

        {sectionHeader('About')}
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-surface)' }}>
          <div
            className="flex items-center justify-between px-4 h-14 border-b"
            style={{ borderColor: 'rgba(255,255,255,0.08)' }}
          >
            <span className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
              WedTrack
            </span>
            <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              v1.0
            </span>
          </div>
          <button
            onClick={() => {
              window.location.href = 'mailto:support@wedtrack.app';
            }}
            className="w-full flex items-center px-4 h-14 border-b text-left cursor-pointer"
            style={{
              borderColor: 'rgba(255,255,255,0.08)',
              color: 'var(--color-primary)',
              background: 'transparent',
              border: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <span className="text-sm font-medium">Send Feedback</span>
          </button>
          <button
            onClick={() => setShowResetDialog(true)}
            className="w-full flex items-center px-4 h-14 text-left cursor-pointer"
            style={{ background: 'transparent', border: 'none' }}
          >
            <span className="text-sm font-medium" style={{ color: '#EF4444' }}>
              Reset Timeline
            </span>
          </button>
        </div>
      </main>

      <ConfirmDialog
        open={showResetDialog}
        onOpenChange={(open) => {
          if (!open) setShowResetDialog(false);
        }}
        title="Reset Timeline"
        description="This will permanently delete all events. Are you sure?"
        confirmLabel={isResetting ? 'Resetting...' : 'Reset'}
        variant="destructive"
        onConfirm={handleReset}
      />
    </div>
  );
}
